import * as React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  checked?: boolean;
  /**
   * The originating event is passed through so callers can read modifier keys —
   * a checkbox list with shift-range selection needs `event.shiftKey`, and the
   * component's own onClick would otherwise swallow it.
   */
  onCheckedChange?: (checked: boolean, event: React.MouseEvent<HTMLButtonElement>) => void;
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, checked, onCheckedChange, disabled, id, ...props }, ref) => {
    return (
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        disabled={disabled}
        id={id}
        className={cn(
          'peer h-4 w-4 shrink-0 border border-border',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground/20',
          'disabled:cursor-not-allowed disabled:opacity-50',
          checked ? 'bg-primary text-primary-foreground border-primary' : 'bg-background',
          className
        )}
        onClick={(event) => onCheckedChange?.(!checked, event)}
      >
        {checked && (
          <Check className="h-3 w-3 mx-auto" />
        )}
        <input
          type="checkbox"
          ref={ref}
          checked={checked}
          onChange={(e) =>
            onCheckedChange?.(e.target.checked, e as unknown as React.MouseEvent<HTMLButtonElement>)
          }
          disabled={disabled}
          className="sr-only"
          {...props}
        />
      </button>
    );
  }
);
Checkbox.displayName = 'Checkbox';

export { Checkbox };
